import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { List, ListItem } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { TextFieldCustom } from '../helpers/formFields';
import { formatString } from './valueFunctions';
import { formatNumberThsndAndDec } from '../helpers/stringFormat';

const AddOperatingCostItemForm = forwardRef(({ costTypes, filterCosts, queryResult, formActions, formResultActions, onSubmit, onSubmitResult }, ref) => {
    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleTypeChange = (event) => {
        formik.setFieldValue('cost_type', event.target.value)
    };

    const initialValues = {
        id: null,
        cost_type: Object.keys(costTypes)[0],
        cost_sub_type: '',
        multiple: costTypes[Object.keys(costTypes)[0]]['multiple'],
        frequency: costTypes[Object.keys(costTypes)[0]]['frequency'],
        wish: '',
    }

    const queryValues = {
        id: queryResult?.id,
        cost_type: queryResult?.cost_type || '',
        cost_sub_type: queryResult?.cost_item?.name || '',
        currency: queryResult?.cost_item?.currency || '€',
        lower: queryResult?.cost_item?.lower || '0',
        higher: queryResult?.cost_item?.higher || '0',
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const isLarger = (value1, value2) => {
        return parseFloat(value2.toString().replace(/,/g, '')) > parseFloat(value1.toString().replace(/,/g, ''));
    };

    const validate = (values) => {
        const errors = {};

        if (!values.cost_sub_type) {
            errors.cost_sub_type = 'Cost Sub Type is needed';
        }

        return errors
    }

    const validateResult = (values) => {
        const errors = {};

        if (!values.cost_sub_type) {
            errors.cost_sub_type = 'Cost Sub Type is needed';
        }

        if (!isNumeric(values.lower)) {
            errors.lower = 'Must be numeric value';
        } else if (!isPositive(values.lower)) {
            errors.lower = 'Must be positive or zero';
        }

        if (!isNumeric(values.higher)) {
            errors.higher = 'Must be numeric value';
        } else if (!isPositive(values.higher)) {
            errors.higher = 'Must be positive';
        } else if (!isLarger(values.lower, values.higher)) {
            errors.higher = 'Must be higher than lower range';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    const formikResult = useFormik({
        initialValues: queryValues,
        validate: validateResult,
        onSubmit: onSubmitResult,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
        },
    }));

    useEffect(() => {
        formik.setFieldValue("multiple", costTypes[formik.values.cost_type]["multiple"])
        formik.setFieldValue("frequency", costTypes[formik.values.cost_type]["frequency"])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.cost_type])

    useEffect(() => {
        if (Object.keys(queryResult).length > 0) {
            formikResult.setFieldValue("id", queryResult?.id)
            formikResult.setFieldValue("cost_type", queryResult?.cost_type)
            formikResult.setFieldValue("cost_sub_type", queryResult?.cost_item?.name)
            formikResult.setFieldValue("currency", queryResult?.cost_item?.currency)
            formikResult.setFieldValue("lower", formatNumberThsndAndDec(queryResult?.cost_item?.lower))
            formikResult.setFieldValue("higher", formatNumberThsndAndDec(queryResult?.cost_item?.higher))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryResult])

    if (Object.keys(queryResult).length > 0) {
        return (
            <Box component="form" onSubmit={formikResult.handleSubmit}>
                <Grid container columnGap={1}>
                    <Grid item xs={8}>
                        <Grid container rowGap={2} columnGap={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption">
                                    Name Cost Sub Type
                                </Typography>
                                <TextFieldCustom
                                    name="cost_sub_type"
                                    value={formikResult.values.cost_sub_type}
                                    onChange={formikResult.handleChange}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, "cost_sub_type") && getIn(formikResult.touched, "cost_sub_type")) && Boolean(getIn(formikResult.errors, "cost_sub_type"))}
                                    helperText={getIn(formikResult.touched, "cost_sub_type") && getIn(formikResult.errors, "cost_sub_type")}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4} />
                            <Grid item xs={5}>
                                <Stack direction="column">
                                    <Typography variant="caption">
                                        Lower Range
                                    </Typography>
                                    <TextFieldCustom
                                        name="lower"
                                        startAdornment={formikResult.values.currency}
                                        value={formikResult.values.lower}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formikResult.setFieldValue("lower", formattedValue || '')
                                        }}
                                        onBlur={formikResult.handleBlur}
                                        error={getIn(formikResult.errors, "lower") && Boolean(getIn(formikResult.errors, "lower"))}
                                        helperText={getIn(formikResult.touched, "lower") && getIn(formikResult.errors, "lower")}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={5}>
                                <Stack direction="column">
                                    <Typography variant="caption">
                                        Higher Range
                                    </Typography>
                                    <TextFieldCustom
                                        name="higher"
                                        startAdornment={formikResult.values.currency}
                                        value={formikResult.values.higher}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formikResult.setFieldValue("higher", formattedValue || '')
                                        }}
                                        onBlur={formikResult.handleBlur}
                                        error={getIn(formikResult.errors, "higher") && Boolean(getIn(formikResult.errors, "higher"))}
                                        helperText={getIn(formikResult.touched, "higher") && getIn(formikResult.errors, "higher")}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                {formResultActions()}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={3}>
                        <Paper elevation={3} sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Description:
                            </Typography>
                            <Typography variant="caption" sx={{ mb: 1 }}>
                                {queryResult?.cost_item?.description}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        )
    }

    return (
        <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container columnGap={1}>
                <Grid item xs={8}>
                    <Grid container rowGap={2} columnGap={1}>
                        <Grid item xs={6}>
                            <Stack direction="column">
                                <Typography variant="caption">
                                    Select Cost Type
                                </Typography>
                                <Box>
                                    <Select
                                        open={open}
                                        onClose={handleClose}
                                        onOpen={handleOpen}
                                        value={formik.values.cost_type}
                                        onChange={handleTypeChange}
                                        size='small'
                                    >
                                        {Object.keys(costTypes).map((cost) => (
                                            !filterCosts.includes(cost) &&
                                            <MenuItem key={cost} value={cost}>
                                                {formatString(cost)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={6}>
                            <Typography variant="caption">
                                Input Cost Sub Type to request
                            </Typography>
                            <TextFieldCustom
                                name="cost_sub_type"
                                value={formik.values.cost_sub_type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(getIn(formik.errors, "cost_sub_type") && getIn(formik.touched, "cost_sub_type")) && Boolean(getIn(formik.errors, "cost_sub_type"))}
                                helperText={getIn(formik.touched, "cost_sub_type") && getIn(formik.errors, "cost_sub_type")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={6}>
                            <Typography variant="caption">
                                Multiple
                            </Typography>
                            <TextFieldCustom
                                name="multiple"
                                value={formik.values.multiple}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "multiple") && Boolean(getIn(formik.errors, "multiple"))}
                                helperText={getIn(formik.touched, "multiple") && getIn(formik.errors, "multiple")}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption">
                                Frequency
                            </Typography>
                            <TextFieldCustom
                                name="frequency"
                                value={formik.values.frequency}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "frequency") && Boolean(getIn(formik.errors, "frequency"))}
                                helperText={getIn(formik.touched, "frequency") && getIn(formik.errors, "frequency")}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption">
                                Specify a wish if you would like to put your request in some context
                            </Typography>
                            <TextFieldCustom
                                name="wish"
                                value={formik.values.wish}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "wish") && Boolean(getIn(formik.errors, "wish"))}
                                helperText={getIn(formik.touched, "wish") && getIn(formik.errors, "wish")}
                                fullWidth
                            />
                            <Typography variant="caption">
                                E.g. "Large company of type X producing products of type Y"
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, mb: 4 }}>
                        {formActions()}
                    </Box>
                </Grid>

                <Grid item xs={3}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Existing Cost Sub Types:
                        </Typography>
                        <List sx={{ padding: 0, listStyleType: 'disc', listStylePosition: 'inside' }}>
                            {costTypes[formik.values.cost_type]["multiple_type"].map((cost_sub_type, index) => (
                                <ListItem
                                    key={index}
                                    sx={{
                                        display: 'list-item',
                                        paddingY: 0.5,
                                        paddingLeft: 2,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 'normal' }}>
                                        {formatString(cost_sub_type)}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    )
})

export default AddOperatingCostItemForm
